<template>
  <div class="master-order-selector">
    <nav class="nav-bar-alternative">
      <ul class="nav-bar-nav">
        <li
          v-for="(order, index) in orders"
          :id="`master-order-item-${index}`"
          :ref="(el) => $emit('set-selector-ref', el, index)"
          :key="order.id || index"
          class="nav-item"
          :class="[{ selected: index === selectedOrderIndex }]"
          v-on:click="selectOrder(index)">
          <div class="content-cnt">
            <div class="labels">
              <p class="title">
                {{ order.title }}
              </p>
              <p class="second">
                {{ order.subTitle }}
              </p>
            </div>
            <div v-if="index === selectedOrderIndex" class="action-icons">
              <div v-if="enableEditButton" class="ico ico-edit" v-on:click="editOrder(index)"></div>
              <template v-if="showCloneButton">
                <BackstageTooltip
                  v-if="isAtMaxOrders"
                  :text="$t('matrix.error.max_bulk_orders_reached', { max: MAX_BULK_DRAFT_ORDERS })">
                  <div class="ico ico-duplicate ico-disabled"></div>
                </BackstageTooltip>
                <div v-else class="ico ico-duplicate" v-on:click="cloneOrder(index)"></div>
              </template>
              <div v-if="enableDeleteButton" class="ico ico-delete" v-on:click="deleteOrder(index)"></div>
            </div>
          </div>
        </li>
      </ul>
      <template v-if="enableAddButton">
        <BackstageTooltip
          v-if="isAtMaxOrders"
          :text="$t('matrix.error.max_bulk_orders_reached', { max: MAX_BULK_DRAFT_ORDERS })">
          <BackstageButton icon-right="addition-sign" :disabled="true" />
        </BackstageTooltip>
        <BackstageButton v-else icon-right="addition-sign" v-on:click="addNewOrder" />
      </template>
    </nav>
  </div>
</template>

<script lang="ts">
import { MAX_BULK_DRAFT_ORDERS } from '@onrunning/backstage-models';
import { type PropType, defineComponent } from 'vue';
import { MasterOrderSelectorModel } from '../../models/master-order-selector.model';
import BackstageButton from '../common/BackstageButton.vue';
import BackstageTooltip from '../common/BackstageTooltip.vue';

export default defineComponent({
  name: 'master-order-selector',
  components: {
    BackstageButton,
    BackstageTooltip,
  },
  props: {
    orders: {
      type: Array as PropType<MasterOrderSelectorModel[]>,
      default: () => [],
    },
    enableAddButton: {
      type: Boolean,
      default: false,
    },
    enableEditButton: {
      type: Boolean,
      default: false,
    },
    enableCloneButton: {
      type: Boolean,
      default: false,
    },
    enableDeleteButton: {
      type: Boolean,
      default: false,
    },
    selectedOrderIsValid: {
      type: Boolean,
      default: false,
    },
    isAtMaxOrders: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { MAX_BULK_DRAFT_ORDERS };
  },
  data() {
    const selectedIndex = this.orders.findIndex((order: MasterOrderSelectorModel) => order.selected);

    return {
      selectedOrderIndex: selectedIndex !== -1 ? selectedIndex : 0,
    };
  },
  computed: {
    showCloneButton(): boolean {
      const isEmptyOrder = this.orders[this.selectedOrderIndex].subTitle.startsWith('0');

      return this.enableCloneButton && !isEmptyOrder;
    },
  },
  methods: {
    selectOrder(index: number) {
      if (!this.selectedOrderIsValid) {
        return;
      }

      this.selectedOrderIndex = index;
      this.$emit('select-order', index);
    },
    addNewOrder() {
      this.$emit('add-new-order');
    },
    editOrder(index: number) {
      this.$emit('edit-order', index);
    },
    deleteOrder(index: number) {
      this.$emit('delete-order', index);
    },
    cloneOrder(index: number) {
      this.$emit('clone-order', index);
    },
  },
});
</script>

<style lang="scss" scoped>
.master-order-selector {
  width: 100%;
  max-width: 1440px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.nav-bar-alternative {
  margin: 0 auto;
  width: 100%;
  min-height: 72px;
  padding: 12px 20px;
  background-color: var(--primary-bg-color);

  @include breakpoint(small) {
    width: 80%;
    margin: 0;
  }

  .nav-bar-nav {
    background: transparent;
    padding: 0;
    margin: 0;
    border-radius: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;

    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--border-color);
      border-radius: 4px;
    }

    .nav-item {
      margin: 0;
      border-radius: 4px;
      border: none;
      background-color: var(--field-bg-color);
      padding: 8px 12px;
      display: flex;
      align-items: center;
      transition: background-color 0.2s ease-in-out;
      cursor: pointer;
      min-width: max-content;

      &.selected {
        background-color: var(--selected-field-bg-color);
      }

      &:hover:not(.selected) {
        background-color: var(--hover-field-bg-color);
      }

      .content-cnt {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 8px;

        .labels {
          display: flex;
          flex-direction: column;
          align-items: flex-start;

          p.title {
            font-size: 1rem;
            font-weight: 500;
            line-height: 1.1;
            margin: 0;
            color: var(--primary-text-color);
          }

          p.second {
            font-size: 0.875rem;
            font-weight: 400;
            line-height: 1.5;
            margin: 0;
            color: var(--dark-gray-color);
          }
        }

        .action-icons {
          display: flex;
          gap: 4px;
          align-items: center;

          .ico {
            width: 1.5rem;
            height: 1.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      }
    }
  }
}

.ico-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
